import { computed } from "vue";
import { useRoute } from "vue-router";
import { useNuxtApp } from "#app";
import {
  createError,
  useAsyncData,
  useRuntimeConfig,
  useRequestURL,
} from "#imports";
import { initializeApp, getApps } from "firebase/app";
import {
  getFirestore as getFirestoreLite,
  doc as docLite,
  getDoc as getDocLite,
  collection as collectionLite,
  query as queryLite,
  where as whereLite,
  limit as limitLite,
  getDocs as getDocsLite,
} from "firebase/firestore/lite";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
} from "firebase/firestore";

/* ---- Firestore → plain-JSON helpers ---- */

function isFsTimestamp(x) {
  return (
    x &&
    typeof x === "object" &&
    typeof x.toDate === "function" &&
    typeof x.seconds === "number" &&
    typeof x.nanoseconds === "number"
  );
}

function toJsonSafe(value) {
  if (isFsTimestamp(value)) return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(toJsonSafe);
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = toJsonSafe(v);
    return out;
  }
  return value;
}

/* ====================================================================
 *  useProductFetch
 *  – завантажує товар за ID або slug (SSR + client)
 *  – повертає { product, param, siteUrl }
 *
 *  БЕЗ ГЛОБАЛЬНОГО ЛОАДЕРА (п.9г), як і головна після п.9б. Він
 *  стартував ще в setup, тобто НА СЕРВЕРІ, і `GlobalLoader` через
 *  teleport потрапляв у сирий HTML: `fixed inset-0 bg-white/80` поверх
 *  усього, що сервер уже відрендерив. Тобто SSR-контент був у HTML, але
 *  користувач до гідрації бачив біле полотно зі спінером (фолбек-таймер
 *  1200 мс). Заміряно перед п.9г: `лоадер Є` на кожній сторінці товару
 *  проти `нема` на головній і каталозі.
 * ==================================================================== */
export async function useProductFetch() {
  const route = useRoute();
  const { $db } = useNuxtApp();
  const cfg = useRuntimeConfig();
  const reqUrl = useRequestURL();

  const siteUrl = String(
    cfg.public?.siteUrl || reqUrl.origin || "https://mulberryss.shop",
  ).replace(/\/$/, "");

  /* ---- Fetch by ID or slug ---- */
  const param = computed(() => String(route.params.id || ""));

  async function fetchByIdOrSlug(val) {
    // Server: Firestore Lite (SSR-safe, real HTML for bots)
    if (import.meta.server) {
      const pub = cfg.public || {};
      const fbConfig = {
        apiKey: pub.firebaseApiKey,
        authDomain: pub.firebaseAuthDomain,
        projectId: pub.firebaseProjectId,
        storageBucket: pub.firebaseStorageBucket,
        messagingSenderId: pub.firebaseMessagingSenderId,
        appId: pub.firebaseAppId,
        measurementId: pub.firebaseMeasurementId,
      };

      try {
        if (!getApps().length) initializeApp(fbConfig);
        const dbLite = getFirestoreLite();

        try {
          const snap = await getDocLite(docLite(dbLite, "products", val));
          if (snap.exists()) return toJsonSafe({ id: snap.id, ...snap.data() });
        } catch {
          /* ignore */
        }

        const qs = await getDocsLite(
          queryLite(
            collectionLite(dbLite, "products"),
            whereLite("slug", "==", val),
            limitLite(1),
          ),
        );
        if (!qs.empty) {
          const d = qs.docs[0];
          return toJsonSafe({ id: d.id, ...d.data() });
        }
      } catch {
        /* fallback to client */
      }
      return null;
    }

    // Client: injected Firestore instance
    try {
      const snap = await getDoc(doc($db, "products", val));
      if (snap.exists()) return toJsonSafe({ id: snap.id, ...snap.data() });
    } catch {
      /* ignore */
    }
    const qs = await getDocs(
      query(collection($db, "products"), where("slug", "==", val), limit(1)),
    );
    if (!qs.empty) {
      const d = qs.docs[0];
      return toJsonSafe({ id: d.id, ...d.data() });
    }
    return null;
  }

  const { data: productRaw } = await useAsyncData(
    () => `product-${param.value}`,
    async () => {
      const p = await fetchByIdOrSlug(param.value);
      if (!p)
        throw createError({
          statusCode: 404,
          statusMessage: "Товар не знайдено",
        });
      return p;
    },
    { watch: [param], server: true },
  );

  const product = computed(() => productRaw.value || {});

  return { product, param, siteUrl };
}
